import React, { useState, useEffect, ReactElement } from 'react';
import Box from '@mui/material/Box';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { InputAdornment, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { countries as options, CountryType } from './CountryCodes';
import { FlagSharp } from '@mui/icons-material';
import { RequiredLabel } from '../../components/massive-bio/ClinicalTrialsSignupForm';
import { Typography } from '@mui/material';

const postalCodeLabels = {
  US: 'ZIP Code',
  CA: 'Postal Code',
  GB: 'Postcode',
  AU: 'Postcode',
  DE: 'Postleitzahl (PLZ)',
  FR: 'Code Postal',
  JP: 'Postal Code',
  CN: '邮政编码 (Yóuzhèng biānmǎ)',
  IN: 'PIN Code',
  IT: 'CAP',
  ES: 'Código Postal',
  ZA: 'Postal Code',
  BR: 'CEP',
  MX: 'Código Postal',
  NL: 'Postcode',
  RU: 'Почтовый индекс',
};


export interface CountrySelectProps {
  onChange: ((event: React.SyntheticEvent<Element, Event> | undefined | null, value: CountryType | undefined | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any> | undefined) => void) | undefined
  error: boolean
  helperText: string | undefined
  value: CountryType | undefined | null
  isOpen?: boolean
  displayField?: 'label' | 'code' | 'phone';
  required?: boolean;
}


export const CountrySelect: React.FC<CountrySelectProps> = ({ onChange, error, helperText, value, isOpen, displayField = 'label', required = false }) => {
  const [inputChanged, setInputChanged] = React.useState(false);
  const [shouldOpen, setShouldOpen] = useState(isOpen ?? false);
  const [preClickShouldOpen, setPreClickShouldOpen] = useState(shouldOpen);
  const [errorCountry, setErrorCountry] = useState(false);
  const [flagLoadingState, setFlagLoadingState] = useState({ isLoading: true, isSuccess: false, isError: false });
  const [errorFlagsUrl, setErrorFlagsUrl] = useState<string | null>(null);
  const [loadedFlags, setLoadedFlags] = useState<{ [key: string]: ReactElement}>({}); 
  
  const [inputValue, setInputValue] = React.useState(value ? value[displayField || 'label'] : '');
  const [selectedCode, setSelectedCode] = React.useState<string | undefined | null>((value?.code) ? value?.code : null)
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const flagsReady = flagLoadingState.isSuccess

  React.useEffect(() => {
    const preloadImagesAndSvgs = async () => {
      const loadPromises = options.map(country => {
        const url = `https://flagcdn.com/${country.code.toLowerCase()}.svg`
        return new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = url;
          img.onload = () => {
            const flagImage = (
              <img
                key={country.code}
                src={url}
                alt="Flag"
                style={{ margin: '4px 8px 4px 0', maxWidth: '28px', maxHeight: '18px' }}
              />)
            country.flagJSX = flagImage
            setLoadedFlags((prev) => ({
              ...prev,
              [country.code]: flagImage, 
            }));
            resolve();
          };
          img.onerror = () => {
            reject(new Error(`Failed to load image: ${url}`));
          };
        });
      });
      try {
        await Promise.all(loadPromises);
        setFlagLoadingState({ ...flagLoadingState, isSuccess: true }); 
      } catch (err: unknown) { 
        if (err instanceof Error) {
          setErrorFlagsUrl(err.message); 
        } else {
          setErrorFlagsUrl('An unknown error occurred'); 
        }
        setFlagLoadingState({ ...flagLoadingState, isError: true });
      }
    };
    preloadImagesAndSvgs();
  }, []); 

  React.useEffect(() => {
    if (isOpen !== undefined) { 
        setShouldOpen(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    setInputValue(value ? value[displayField || 'label'] : '');
  }, [value, displayField]);

  useEffect(() => {
    setSelectedCode(value?.code)
  }, [value])



  const onChangeLocal: ((event: React.SyntheticEvent<Element, Event> | undefined | null, value: any | undefined, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any> | undefined) => void) = (event, value, reason, details) => {

    setSelectedCode((value?.code))
    if (onChange) {
      onChange(event, value, reason, details)
    }
  };

  const handleOpen = (event: React.SyntheticEvent) => {
    const shouldOpenLocal = value?.[displayField || 'label'] !== inputValue && !!options.length
    if (event.type == "click") {
      setShouldOpen(preClickShouldOpen || !!value);
    }
  };

  const handleClose = (event: React.SyntheticEvent, reason: string) => {
    if (event.type == "click") {
      setPreClickShouldOpen(shouldOpen)
      setShouldOpen(false);
    }
  };

  console.log('should open? ', shouldOpen)
  return (
    <>
      <Autocomplete
        id="country-select-demo"
        sx={{ width: '100%' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              backgroundColor: 'rgba(244, 240, 255, 0.97)',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 4px 16px rgba(80, 60, 160, 0.12)',
              border: '1px solid rgba(164, 142, 233, 0.15)',
              mt: 0.5,
            },
          },
          listbox: {
            sx: {
              py: 0.5,
              maxHeight: 220,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(101,110,255,0.18)', borderRadius: 2, '&:hover': { background: 'rgba(101,110,255,0.3)' } },
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(101,110,255,0.18) transparent',
              '& .MuiAutocomplete-option': {
                fontSize: '0.85rem',
                py: 0.75,
                px: 1.5,
                minHeight: 'auto',
                borderRadius: '6px',
                mx: 0.5,
                '&[aria-selected="true"]': {
                  backgroundColor: 'rgba(101,110,255,0.1)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(101,110,255,0.06)',
                },
              },
            },
          },
        }}
        options={options}
        autoHighlight
        open={shouldOpen}
        onOpen={handleOpen} 
        onClose={handleClose} 
        getOptionLabel={(option: any) => option[displayField || 'label']}
        isOptionEqualToValue={(option: CountryType, value: CountryType) => {
          return option && value && option[displayField || 'label'] === value[displayField || 'label'];
        }}
        value={value}
        onChange={onChangeLocal}
        inputValue={inputValue ?? ''}
        onInputChange={(event, newInputValue) => {
          if (event && (event.type === 'change' || event.type === 'click' || event.type === "keydown")) {
            setInputValue(newInputValue);  
            const matches = options.filter(option =>
              option[displayField || 'label'].toLowerCase() === newInputValue.toLowerCase()
            );
            const includes = options.some(option =>
              option[displayField || 'label'].toLowerCase().includes(newInputValue.toLowerCase())
            );
            console.log(includes)
            setInputChanged(!matches.length && newInputValue !== (value ? value[displayField || 'label'] : ''))
            const isErrorCountry = !includes && !!newInputValue?.length
            setErrorCountry(isErrorCountry);
            console.log(includes, !matches, newInputValue !== (value ? value[displayField || 'label'] : ''), !!options.length)

            setShouldOpen(includes && matches.length < 1 && newInputValue !== (value ? value[displayField || 'label'] : '') && !!options.length);
            if(matches.length === 1) {
              setInputValue(matches[0][displayField || 'label']);
              onChangeLocal( undefined, matches[0], 'selectOption', undefined)

            }
          }
          if (!(value ? value[displayField || 'label'] : '') || (value ? value[displayField || 'label'] : '') === newInputValue) {
            setSelectedCode(value?.code)
          } else {
            onChangeLocal(null, null, 'clear', undefined)
            setSelectedCode(null)
          }

        }}
        onBlur={() => {
          if (inputChanged && value && (value ? value[displayField || 'label'] : '') !== inputValue) { 
            onChangeLocal(null, null, 'clear', undefined) 
          }
        }}
        renderOption={(props, option: any,) => (
          <Box
          // key={option.code}
           component="li" sx={{ '& > img': { mr: 1, flexShrink: 0 }, fontSize: '0.85rem' }} {...props}>
            {flagsReady && option.flagJSX}
            {option[displayField || 'label']}
          </Box>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => {
          const { value } = params.inputProps
          const { color, size, ...inputPropsSansColor } = params.inputProps
          const helperTextCombined = `${helperText ? helperText : ''}${errorCountry ? `${helperText ? '. ' : ''}No country matching ${inputValue}` : ``}`
          
          const labelText = displayField === 'phone' ? 'Country Code' : 'Select country';
          const finalLabel = required ? <RequiredLabel label={labelText} /> : labelText;
          
          return (
            <>
              <TextField
                inputRef={inputRef}
                {...params}
                error={errorCountry || !!error}
                helperText={helperTextCombined}
                label={finalLabel}
                fullWidth
                InputProps={{
                  ...params.InputProps, autoComplete: 'new-password',
                  startAdornment: <InputAdornment position="start"> <>
                    {selectedCode &&
                      <img
                        src={`https://flagcdn.com/${selectedCode.toLowerCase()}.svg`}
                        width="45"
                        alt={selectedCode}></img>
                    }

                  </></InputAdornment>,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />


            </>
          )
        }}
      />
    </>);
}
